// components/dashboard/EnquiryTable.jsx
import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessIcon from "@mui/icons-material/Business";
import InboxIcon from "@mui/icons-material/Inbox";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { T } from "../../../theme";

const sq = { borderRadius: "4px !important" };

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatTime = (d) =>
  d
    ? new Date(d).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const getInitials = (name) =>
  name
    ?.split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) ?? "?";

const getAvatarColor = (name) => {
  const colors = [
    T.primary,
    "#7c3aed",
    "#0891b2",
    "#059669",
    "#d97706",
    T.red,
  ];
  return colors[(name?.charCodeAt(0) ?? 0) % colors.length];
};

// ── ActionButton (matching StaffList style) ────────────────────────
const ActionButton = ({ tip, icon, isDelete = false, isView = false, onClick }) => {
  const color = isDelete ? T.red : isView ? T.info : T.primary;
  const bg = isDelete ? T.redBg : isView ? T.infoBg : T.primaryBg;
  const border = isDelete ? T.redBorder : isView ? T.infoBorder : T.border;
  const hoverBg = isDelete ? T.redBg : isView ? T.infoBg : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 20,
          height: 20,
          ...sq,
          color,
          bgcolor: bg,
          border: `1px solid ${border}`,
          transition: "all 0.18s",
          "&:hover": {
            bgcolor: hoverBg,
            borderColor: color,
            transform: "translateY(-1px)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 12 } })}
      </IconButton>
    </Tooltip>
  );
};

// ── Skeleton Row ───────────────────────────────────────────────────
const SkeletonRow = () => (
  <TableRow>
    {Array.from({ length: 8 }).map((_, i) => (
      <TableCell key={i} sx={{ py: "6px", px: 1.5 }}>
        <Box
          sx={{
            height: 14,
            bgcolor: T.white,
            ...sq,
            animation: "pulse 1.5s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.4 },
            },
            width: i === 0 ? "50%" : i === 4 ? "70%" : "80%",
          }}
        />
      </TableCell>
    ))}
  </TableRow>
);

// ── Main EnquiryTable ──────────────────────────────────────────────
const EnquiryTable = ({ enquiries, loading, onView, onDelete }) => {
  const columns = [
    { label: "#", align: "center", sticky: true, left: 0, minW: 48 },
    { label: "Status", align: "center", minW: 80 },
    { label: "Name", align: "left", sticky: true, left: 48, minW: 180 },
    { label: "Email", align: "left", minW: 200 },
    { label: "Phone", align: "center", minW: 130 },
    { label: "Company", align: "left", minW: 160 },
    { label: "Message", align: "left", minW: 200 },
    { label: "Date", align: "center", minW: 110 },
    { label: "Actions", align: "center", sticky: true, right: 0, minW: 100 },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        ...sq,
        border: `1.5px solid ${T.border}`,
        overflow: "hidden",
        boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
      }}
    >
      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 10,
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: T.primary }} size={36} />
          <Typography
            sx={{ fontSize: "0.75rem", color: T.textSub, fontWeight: 500 }}
          >
            Loading enquiries...
          </Typography>
        </Box>
      ) : (
        <TableContainer
          sx={{ maxHeight: "calc(100vh - 310px)", overflow: "auto" }}
        >
         <Table
  stickyHeader
  size="small"
  sx={{
    minWidth: 1100,
    borderCollapse: "collapse",
    "& td, & th": {
      py: 0,
      px: 1,
      fontSize: "0.75rem",
      borderBottom: `1px solid #ccc`,
      lineHeight: 1.2,
    },
    "& th": {
      fontWeight: 700,
      color: T.headerText,
      bgcolor: `${T.headerBg} !important`,
      py: 0.2,
      height: 28,
      fontSize: "0.8rem",
      letterSpacing: "0.01em",
      borderBottom: `1px solid #ccc`,
      lineHeight: 1.2,
      whiteSpace: "nowrap",
    },
    "& td": {
      height: 26,
    },
  }}
>
            {/* ── Head ── */}
            <TableHead>
              <TableRow>
              {columns.map(({ label, align, sticky, left, right, minW }) => (
  <TableCell
    key={label}
    align={align}
    sx={{
      minWidth: minW,
      bgcolor: `${T.headerBg} !important`,
      color: `${T.headerText} !important`,
      ...(sticky && {
        position: "sticky",
        ...(left !== undefined && { left }),
        ...(right !== undefined && { right }),
        zIndex: 12,
      }),
    }}
  >
    {label}
  </TableCell>
))}
              </TableRow>
            </TableHead>

            {/* ── Body ── */}
            <TableBody>
              {/* Empty State */}
              {enquiries.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    sx={{ textAlign: "center", py: 8, border: "none" }}
                  >
                    <InboxIcon
                      sx={{
                        fontSize: 44,
                        color: T.border,
                        mb: 1,
                        display: "block",
                        mx: "auto",
                      }}
                    />
                    <Typography
                      sx={{
                        color: T.textSub,
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        mb: 0.4,
                      }}
                    >
                      No enquiries found
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: T.textMuted }}>
                      Try adjusting your search or filter
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                enquiries.map((enquiry, idx) => {
  const isEven = idx % 2 === 0;
  const rowBg = isEven ? T.baseRow : T.altRow;

  return (
    <TableRow
      key={enquiry.id}
      onClick={() => onView(enquiry)}
      sx={{
        cursor: "pointer",
        "& > td": {
          bgcolor: rowBg,
          transition: "background-color 0.12s",
        },
        "&:hover > td": {
          bgcolor: `${T.rowHover} !important`,
        },
      }}
    >
      {/* S.No */}
      <TableCell
        align="center"
        sx={{
          position: "sticky",
          left: 0,
          bgcolor: `${rowBg} !important`,
          zIndex: 5,
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: T.textMuted }}>
          {idx + 1}
        </Typography>
      </TableCell>

      {/* Status */}
      <TableCell align="center">
        <Chip
          label={enquiry.is_read ? "Read" : "New"}
          size="small"
          sx={{
            fontSize: "0.6rem",
            height: 16,
            fontWeight: 700,
            ...sq,
            bgcolor: enquiry.is_read ? "rgba(107,114,128,0.1)" : T.primaryBg,
            color: enquiry.is_read ? T.textSub : T.primary,
            border: `1px solid ${enquiry.is_read ? "#e5e7eb" : T.border}`,
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      </TableCell>

      {/* Name */}
      <TableCell
        align="left"
        sx={{
          position: "sticky",
          left: 48,
          bgcolor: `${rowBg} !important`,
          zIndex: 5,
          py: "2px !important",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            sx={{
              width: 20,
              height: 20,
              ...sq,
              bgcolor: getAvatarColor(enquiry.name),
              fontSize: "0.55rem",
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            {getInitials(enquiry.name)}
          </Avatar>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: enquiry.is_read ? 500 : 700,
              color: T.text,
              whiteSpace: "nowrap",
            }}
          >
            {enquiry.name || "—"}
          </Typography>
        </Stack>
      </TableCell>

      {/* Email */}
      <TableCell align="left">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <EmailIcon sx={{ fontSize: 11, color: T.primary, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.75rem", color: T.text, fontWeight: 500, whiteSpace: "nowrap" }}>
            {enquiry.email || "—"}
          </Typography>
        </Stack>
      </TableCell>

      {/* Phone */}
      <TableCell align="center">
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
          <PhoneIcon sx={{ fontSize: 11, color: T.primary, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.75rem", color: T.text, fontWeight: 600, fontFamily: "monospace", whiteSpace: "nowrap" }}>
            {enquiry.phone || "—"}
          </Typography>
        </Stack>
      </TableCell>

      {/* Company */}
      <TableCell align="left">
        {enquiry.company ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <BusinessIcon sx={{ fontSize: 11, color: T.textMuted, flexShrink: 0 }} />
            <Typography sx={{ fontSize: "0.75rem", color: T.text, fontWeight: 500, whiteSpace: "nowrap" }}>
              {enquiry.company}
            </Typography>
          </Stack>
        ) : (
          <Typography sx={{ fontSize: "0.7rem", color: T.textMuted, fontStyle: "italic" }}>
            N/A
          </Typography>
        )}
      </TableCell>

      {/* Message */}
      <TableCell align="left" sx={{ maxWidth: 200 }}>
        <Tooltip title={enquiry.message || ""} arrow placement="top">
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: T.textSub,
              fontWeight: 400,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 190,
              display: "block",
            }}
          >
            {enquiry.message || "—"}
          </Typography>
        </Tooltip>
      </TableCell>

      {/* Date */}
      <TableCell align="center">
        <Chip
          label={formatDate(enquiry.created_at)}
          size="small"
          sx={{
            fontSize: "0.6rem",
            height: 16,
            ...sq,
            bgcolor: T.white,
            color: T.primary,
            border: `1px solid ${T.border}`,
            fontWeight: 700,
            "& .MuiChip-label": { px: 0.75 },
            mb: 0.2,
            display: "flex",
          }}
        />
        <Typography sx={{ fontSize: "0.6rem", color: T.textMuted, textAlign: "center", mt: 0.1 }}>
          {formatTime(enquiry.created_at)}
        </Typography>
      </TableCell>

      {/* Actions */}
      <TableCell
        align="center"
        sx={{
          position: "sticky",
          right: 0,
          bgcolor: `${rowBg} !important`,
          zIndex: 5,
          py: "2px !important",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <ActionButton
            tip="View Details"
            icon={<VisibilityIcon />}
            isView
            onClick={() => onView(enquiry)}
          />
          <ActionButton
            tip="Delete"
            icon={<DeleteIcon />}
            isDelete
            onClick={() => onDelete(enquiry.id)}
          />
        </Stack>
      </TableCell>
    </TableRow>
  );
})
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default EnquiryTable;
